import { Note } from '../types/carnetsDeNotes';
import _every from 'lodash-es/every';
import _flatten from 'lodash-es/flatten';
import _omit from 'lodash-es/omit';
import _groupBy from 'lodash-es/groupBy';
import { v4 as uuid } from 'uuid';
import { Eleve } from '@teetsh/app/src/types/eleves';
import { NotationSystem } from '@teetsh/app/src/types/notationSystem';
import { ensureNotesForAllStudents } from '@teetsh/app/src/containers/CarnetsDeNotes/Evaluations/utils/ensureNotesForStudent';

export interface Result {
  newNotes: Note[];
  areSomeNotesDeleted: boolean;
}

export function areAllNotesEmpty(notes: Note[]): boolean {
  // Double equal is on purpose : https://stackoverflow.com/questions/2559318/how-to-check-for-an-undefined-or-null-variable-in-javascript
  return _every(notes, note => note.note == null);
}

export function updateNotesWithNewReferentialItem(
  notesParam: Note[],
  eleves: Eleve[],
  referentialItemIdSelected: string,
  evaluationId: string,
  notationSystem?: NotationSystem,
): Result {
  const eleveIds = eleves.map(e => e.id);
  const notes = ensureNotesForAllStudents(eleveIds, evaluationId, notesParam, {
    notationSystem,
  });
  if (notes.length === 0) {
    return {
      newNotes: [],
      areSomeNotesDeleted: false,
    };
  }

  const groupedByEleveId = _groupBy(notes, t => t.eleveId);
  const arrayOfNotesOfEleves = Object.values(groupedByEleveId);
  const firstEleveNotes = arrayOfNotesOfEleves[0];
  if (firstEleveNotes.length === 1) {
    if (firstEleveNotes[0].referentialItemId === referentialItemIdSelected) {
      return {
        newNotes: notes.map(note => {
          const { referentialItemId, referentialItemValue, ...rest } = note;
          return rest;
        }),
        areSomeNotesDeleted: false,
      };
    }
    if (firstEleveNotes[0].referentialItemId == null) {
      return {
        newNotes: notes.map(note => {
          const { referentialItemId, referentialItemValue, ...rest } = note;
          return { ...rest, referentialItemId: referentialItemIdSelected };
        }),
        areSomeNotesDeleted: false,
      };
    }
    return {
      newNotes: _flatten(
        arrayOfNotesOfEleves.map(notesOfEleves => [
          ...notesOfEleves,
          {
            ..._omit(notesOfEleves[0], 'note'),
            id: uuid(),
            referentialItemId: referentialItemIdSelected,
            notationSystemId: notationSystem?.id,
            notationSystemMin: notationSystem?.minValue,
            notationSystemMax: notationSystem?.maxValue,
          },
        ]),
      ),
      areSomeNotesDeleted: false,
    };
  }
  if (firstEleveNotes.length > 1) {
    if (
      firstEleveNotes
        .map(n => n.referentialItemId)
        .includes(referentialItemIdSelected)
    ) {
      return {
        newNotes: notes.filter(
          note => note.referentialItemId !== referentialItemIdSelected,
        ),
        areSomeNotesDeleted: !areAllNotesEmpty(
          notes.filter(
            note => note.referentialItemId === referentialItemIdSelected,
          ),
        ),
      };
    }

    return {
      newNotes: _flatten(
        arrayOfNotesOfEleves.map(notesOfEleves => [
          ...notesOfEleves,
          {
            ..._omit(notesOfEleves[0], 'note'),
            id: uuid(),
            referentialItemId: referentialItemIdSelected,
            notationSystemId: notationSystem?.id,
            notationSystemMin: notationSystem?.minValue,
            notationSystemMax: notationSystem?.maxValue,
          },
        ]),
      ),
      areSomeNotesDeleted: false,
    };
  }

  return {
    newNotes: notes.map(note => ({
      ...note,
      referentialItemId: referentialItemIdSelected,
    })),
    areSomeNotesDeleted: false,
  };
}
